import { useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactGA from "react-ga4";

import { useDomain } from "@useDomain";

// Layout
import PublicLayout from "./layouts/PublicLayout.jsx";

// Authentication
import Login from "./pages/Auth/Login.jsx";
import Register from "./pages/Auth/Register.jsx";
import VerifyEmail from "./pages/Auth/VerifyEmail.jsx";
import ForgotPassword from "./pages/Auth/ForgotPassword.jsx";
import ResetPassword from "./pages/Auth/ResetPassword.jsx";

// Static
import About from "./pages/Static/About.jsx";
import Privacy from "./pages/Static/Privacy.jsx";
import Terms from "./pages/Static/Terms.jsx";
import NotFound from "./pages/System/NotFound.jsx";

// Directory
import Directory from "./pages/Directory/Public/Home/Directory.jsx";
import LeagueProfile from "./pages/Directory/Public/LeagueProfile/LeagueProfileV2.jsx";
import DirectoryLeagueSchedule from "./pages/Directory/Public/LeagueProfileTabs/LeagueSchedule/DirectoryLeagueSchedule.jsx";
import DirectoryLeagueTeam from "./pages/Directory/Public/LeagueProfileTabs/LeagueTeam/DirectoryLeagueTeam.jsx";
import DirectoryLeagueStanding from "./pages/Directory/Public/LeagueProfileTabs/LeagueStandings/DirectoryLeagueStanding.jsx";
import DirectoryLeaguePlayers from "./pages/Directory/Public/LeagueProfileTabs/LeaguePlayers/DirectoryLeaguePlayers.jsx";
import DirectoryLeagueGame from "./pages/Directory/Public/LeagueProfileTabs/LeagueGame/DirectoryLeagueGame.jsx";
import DirectoryLeaguePlayer from "./pages/Directory/Public/LeagueProfileTabs/LeaguePlayers/DirectoryLeaguePlayer.jsx";
import Wall from "./pages/Directory/Wall/Wall.jsx"
import SportsProfilePage from "./pages/Directory/Profile/SportsProfilePage.jsx";
import Challenges from "./pages/Directory/Challenges/Challenges.jsx";
import SportsCirclePage from "./pages/Directory/Profile/SportsCircle.jsx";
import Messages from "./pages/Directory/Profile/Messages.jsx"


// Organization Website
import DynamicOrgPage from "./components/DynamicOrgPage.jsx";


// Public League Management
import TeamPublic from "./pages/Dashboard/LeagueManagement/Team/Public/TeamPublic.jsx";
import PlayerPublic from "./pages/Dashboard/LeagueManagement/Player/Public/PlayerPublic.jsx";
import GamePublic from "./pages/Dashboard/LeagueManagement/Game/Public/GamePublic.jsx";
import TournamentBracketPublic from "./pages/Dashboard/LeagueManagement/Tournament/Public/TournamentBracket.jsx"
// In Progress
import SandboxDemo from "./pages/InProgress/Sandbox/SandboxDemo.jsx";
import PlayersProfilePage from "./pages/Directory/Profile/SportsProfilePage.jsx";
import PlayersTeamBasketballDemo from "./pages/Dashboard/LeagueManagement/Player/Public/SportPages/Basketball/TeamBasketballPublic.jsx";
import TheField from "./pages/Directory/Profile/TheField.jsx"

function PublicApp() {

  const {
    organization,
    loadingDomain,
    isCustomDomain,
  } = useDomain();

  const location = useLocation();

  const siteTitle =
    organization?.name ||
    organization?.slug ||
    "SportingHip";

  const canUseBrandedSite =
    organization?.plan === "branded_site" ||
    organization?.plan === "custom_domain";

  const listingRedirectPath = "/";

  const brandedOnly = (element) =>
    canUseBrandedSite ? (
      element
    ) : (
      <Navigate to={listingRedirectPath} replace />
    );

  useEffect(() => {
    const slugFromPath = location.pathname.split("/")[1];

    const isHomepage =
      location.pathname === "/" ||
      location.pathname === `/${slugFromPath}`;

    document.body.classList.toggle("homepage", isHomepage);
  }, [location.pathname]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      ReactGA.send({
        hitType: "pageview",
        page_title: document.title || "Untitled",
        page_location: window.location.href,
        page_path: location.pathname + location.search,
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [location.pathname, location.search]);

  if (loadingDomain) return null;

  return (
    <div className="app_container">
      <Helmet>
        <title>{siteTitle}</title>

        <meta
          name="description"
          content={
            organization
              ? `Welcome to ${siteTitle}`
              : "SportingHip – Discover and manage sports leagues"
          }
        />

        <link
          rel="canonical"
          href={window.location.origin + window.location.pathname}
        />
      </Helmet>

      <div className="content_wrapper_public">
        <Routes>
          <Route element={<PublicLayout />}>
            {/* Homepage */}
            <Route
              path="/"
              element={
                organization ? <DynamicOrgPage /> : <Directory />
              }
            />

            {/* Authentication */}
            <Route path="/login" element={<Login />} />
            <Route path="/:slug/login" element={<Login />} />

            <Route
              path="/register"
              element={isCustomDomain ? <NotFound /> : <Register />}
            />

            <Route
              path="/verify-email"
              element={isCustomDomain ? <NotFound /> : <VerifyEmail />}
            />

            <Route
              path="/forgot-password"
              element={isCustomDomain ? <NotFound /> : <ForgotPassword />}
            />

            <Route
              path="/reset-password"
              element={isCustomDomain ? <NotFound /> : <ResetPassword />}
            />

            {/* Static Pages */}
            <Route
              path="/privacy"
              element={isCustomDomain ? <NotFound /> : <Privacy />}
            />

            <Route
              path="/terms"
              element={isCustomDomain ? <NotFound /> : <Terms />}
            />

            <Route
              path="/about"
              element={isCustomDomain ? <NotFound /> : <About />}
            />

            {/* In Progress */}
            <Route path="/demo" element={<SandboxDemo />} />
            <Route path="/pp" element={<PlayersProfilePage />} />

            <Route
              path="/:slug/playersteambasketball/:sltId"
              element={<PlayersTeamBasketballDemo />}
            />

            {/* Directory Browse */}
            <Route path="/state/:state" element={<Directory />} />
            <Route path="/state/:state/sport" element={<Directory />} />
            <Route
              path="/state/:state/sport/:sport"
              element={<Directory />}
            />
            <Route
              path="/state/:state/sport/:sport/audience/:audience"
              element={<Directory />}
            />
            <Route
              path="/state/:state/audience"
              element={<Directory />}
            />
            <Route
              path="/state/:state/audience/:audience"
              element={<Directory />}
            />

            <Route path="/county/:county" element={<Directory />} />
            <Route
              path="/county/:county/sport"
              element={<Directory />}
            />
            <Route
              path="/county/:county/sport/:sport"
              element={<Directory />}
            />

            <Route
              path="/state/:state/county/:county"
              element={<Directory />}
            />
            <Route
              path="/state/:state/county/:county/sport"
              element={<Directory />}
            />
            <Route
              path="/state/:state/county/:county/sport/:sport"
              element={<Directory />}
            />
            <Route
              path="/state/:state/county/:county/audience"
              element={<Directory />}
            />
            <Route
              path="/state/:state/county/:county/audience/:audience"
              element={<Directory />}
            />
            <Route
              path="/state/:state/county/:county/sport/:sport/audience/:audience"
              element={<Directory />}
            />

            <Route path="/sport" element={<Directory />} />
            <Route path="/sport/:sport" element={<Directory />} />
            <Route
              path="/sport/:sport/audience"
              element={<Directory />}
            />
            <Route
              path="/sport/:sport/audience/:audience"
              element={<Directory />}
            />
            <Route
              path="/sport/audience/:audience"
              element={<Directory />}
            />

            <Route path="/audience" element={<Directory />} />
            <Route
              path="/audience/:audience"
              element={<Directory />}
            />

            {/* Directory League Profile */}
            <Route
              path="/leagues/:leagueNameAndId"
              element={<LeagueProfile />}
            />

            <Route
              path="/leagues/:leagueNameAndId/schedule"
              element={<DirectoryLeagueSchedule />}
            />

            <Route
              path="/leagues/:leagueNameAndId/teams/:seasonsLeagueTeamId"
              element={<DirectoryLeagueTeam />}
            />

            <Route
              path="/leagues/:leagueNameAndId/standings"
              element={<DirectoryLeagueStanding />}
            />

            <Route
              path="/leagues/:leagueNameAndId/players"
              element={<DirectoryLeaguePlayers />}
            />

            <Route
              path="/leagues/:leagueNameAndId/games/:gameId"
              element={<DirectoryLeagueGame />}
            />

            <Route
              path="/leagues/:leagueNameAndId/players/:rosterId"
              element={<DirectoryLeaguePlayer />}
            />
            <Route
              path="/messages"
              element={<Messages />}
            />
            <Route
              path="/messages/:conversationId"
              element={<Messages />}
            />
            <Route 
              path="/wall"
              element={<Wall />}
            />

            <Route path="/profile/:id" 
            element={<SportsProfilePage />} 
            />

            <Route path="/challenges"
            element={<Challenges />}
            />

            <Route path="/field"
            element={<TheField />}
            />

            {/* Public Team Profiles */}
            <Route
              path="/:slug/teams/:id"
              element={<TeamPublic />}
            />
            <Route path="/teams/:id" element={<TeamPublic />} />
            <Route
              path="/profile/:id/friends"
              element={<SportsCirclePage />}
            />
            {/* Public Player Profiles */}
            <Route
              path="/:slug/players/:id"
              element={<PlayerPublic />}
            />
            <Route path="/players/:id" element={<PlayerPublic />} />

            {/* Public Game Profiles */}
            <Route
              path="/:slug/games/:id"
              element={<GamePublic />}
            />
            <Route path="/games/:id" element={<GamePublic />} />


            {/* Organization Features */}
            <Route
              path="/:slug/tournamentBracket"
              element={<TournamentBracketPublic />}
            />

            <Route
              path="/tournamentBracket"
              element={<TournamentBracketPublic />}
            />

            {/* Slug Organization Website */}
            {!isCustomDomain && (
              <>
                <Route
                  path="/:slug"
                  element={
                    canUseBrandedSite ? (
                      <DynamicOrgPage />
                    ) : (
                      <Navigate
                        to={listingRedirectPath}
                        replace
                      />
                    )
                  }
                />

                <Route
                  path="/:slug/:pagePath"
                  element={brandedOnly(<DynamicOrgPage />)}
                />
              </>
            )}

            {/* Custom Domain Organization Pages */}
            <Route
              path="/:pagePath"
              element={<DynamicOrgPage />}
            />

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </div>
    </div>
  );
}

export default PublicApp;